import type { DialysisLookupOption, PatientFormFields, ResponsiblePersonFormFields } from './types';
import { doctorOptionLabel } from '../../lib/doctor-name';
import { normalizePhoneInput } from '../../lib/phone-input';

const DIALYSIS_TIMEZONE = 'Asia/Kolkata';

const dialysisDateTimeFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: DIALYSIS_TIMEZONE,
});

const dialysisDateFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit', month: '2-digit', year: 'numeric', timeZone: DIALYSIS_TIMEZONE,
});

const valueForPart = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';

export const formatDialysisDate = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const parts = dialysisDateFormatter.formatToParts(date);
  return `${valueForPart(parts, 'day')}-${valueForPart(parts, 'month')}-${valueForPart(parts, 'year')}`;
};

export const formatDialysisDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const parts = dialysisDateTimeFormatter.formatToParts(date);
  return `${valueForPart(parts, 'day')}-${valueForPart(parts, 'month')}-${valueForPart(parts, 'year')} ${valueForPart(parts, 'hour')}:${valueForPart(parts, 'minute')} ${valueForPart(parts, 'dayPeriod').toUpperCase()}`.trim();
};

export const formatDialysisTime = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  if (!trimmed) return '-';
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(trimmed);
  if (!match) return trimmed;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return trimmed;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${String(displayHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
};

export const todayInput = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10);
};

export const dateTimeInput = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

export const timeInput = (hoursFromNow = 0) => {
  const date = new Date(Date.now() + hoursFromNow * 60 * 60_000);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

export const optionList = (items: DialysisLookupOption[] | undefined) => (items ?? []).map((item) => ({ value: item.id, label: doctorOptionLabel(item.label, item.code) }));

export const ageFromDateOfBirth = (dob: string): { ageYears: number; ageMonths: number; ageDays: number } => {
  const birth = new Date(dob);
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  let days = now.getDate() - birth.getDate();
  if (days < 0) { months -= 1; days += new Date(now.getFullYear(), now.getMonth(), 0).getDate(); }
  if (months < 0) { years -= 1; months += 12; }
  return { ageYears: Math.max(0, years), ageMonths: Math.max(0, months), ageDays: Math.max(0, days) };
};

export const blankPatientFields = (): PatientFormFields => ({
  patientId: null, uhid: null, name: '', phone: null, gender: 'Male',
  maritalStatus: null, guardianName: null, relation: null, guardianContactNo: null, dateOfBirth: null,
  ageYears: 0, ageMonths: 0, ageDays: 0,
  address: null, state: 'West Bengal (WB)', district: null, pinCode: null, aadhaar: null,
});

export const patientFieldsFromOption = (option: DialysisLookupOption): PatientFormFields => {
  let ageYears = 0;
  if (option.dateOfBirth) {
    const birth = new Date(option.dateOfBirth);
    const now = new Date();
    ageYears = Math.max(0, now.getFullYear() - birth.getFullYear() - (now < new Date(now.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0));
  }
  return {
    patientId: option.id,
    uhid: option.code ?? null,
    name: option.label,
    phone: option.phone ? normalizePhoneInput(option.phone) : null,
    gender: option.gender === 'female' ? 'Female' : option.gender === 'male' ? 'Male' : 'Others',
    maritalStatus: null,
    guardianName: null,
    relation: null,
    guardianContactNo: null,
    dateOfBirth: option.dateOfBirth ?? null,
    ageYears,
    ageMonths: 0,
    ageDays: 0,
    address: option.address ?? null,
    state: option.state ?? 'West Bengal (WB)',
    district: option.district ?? null,
    pinCode: option.pinCode ?? null,
    aadhaar: null,
  };
};

export const blankResponsiblePerson = (): ResponsiblePersonFormFields => ({ name: '', relation: '', phone: '', age: null, address: '' });
